// Resume27.jsx
import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Divider,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material';
import { ChevronLeft, ChevronRight, DragIndicator, Reorder } from '@mui/icons-material';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LanguageIcon from '@mui/icons-material/Language';

export const resumeMeta = {
  hasPhoto: false,
  columns: 1,
};

const A4_WIDTH_PX = 794;
const A4_HEIGHT_PX = 1123;
const LETTER_WIDTH_PX = 816;
const LETTER_HEIGHT_PX = 1056;

const defaultSectionOrder = [
  'summary',
  'work-experience',
  'education',
  'skills',
  'certificates',
  'languages',
  'accomplishments',
  'additional-info',
  'software',
  'references',
  'volunteering',
  'interests',
  'websites',
];

const Resume27 = ({
  pageSize = 'A4',
  color = '#1976d2',
  font = "'Roboto', 'Helvetica', 'Arial', sans-serif",
  fontSize = '14px',
  fontStyle = 'normal',
  headingSize = '18px',
  sectionSpacing = 10,
  paragraphSpacing = 10,
  lineSpacing = 20,
  topBottomMargin = 36,
  sideMargins = 46,
  lineWeight = 1,
  exporting = false,
  enableDragDrop = true,
  formData = {},
  linkedIn = '',
}) => {
  const pageW = pageSize === 'A4' ? A4_WIDTH_PX : LETTER_WIDTH_PX;
  const pageH = pageSize === 'A4' ? A4_HEIGHT_PX : LETTER_HEIGHT_PX;

  // ── Pagination states ────────────────────────────────────────────────────
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const measureRef = useRef(null);

  const [sectionOrder, setSectionOrder] = useState(defaultSectionOrder);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [arrangeDialogOpen, setArrangeDialogOpen] = useState(false);

  // Data states
  const [personalData, setPersonalData] = useState({
    firstName: 'AVERY',
    lastName: 'DAVIS',
    city: 'Any City',
    phone: '+123-456-7890',
    email: 'hello@reallygreatsite.com',
    website: '',
    currentPosition: '',
    linkedIn: '',
  });

  const [savedSummary, setSavedSummary] = useState('');
  const [savedSkills, setSavedSkills] = useState([]);
  const [educationEntries, setEducationEntries] = useState([]);
  const [workExperiences, setWorkExperiences] = useState([]);
  const [savedCertifications, setSavedCertifications] = useState([]);
  const [savedLanguages, setSavedLanguages] = useState([]);
  const [savedAccomplishments, setSavedAccomplishments] = useState([]);
  const [savedAdditionalInfo, setSavedAdditionalInfo] = useState([]);
  const [savedSoftware, setSavedSoftware] = useState([]);
  const [savedReferences, setSavedReferences] = useState([]);
  const [savedVolunteering, setSavedVolunteering] = useState([]);
  const [savedInterests, setSavedInterests] = useState([]);
  const [savedWebsites, setSavedWebsites] = useState([]);
  const [customSections, setCustomSections] = useState({});

  // Safe JSON parse helper
  const safeParseJSON = (str, fallback = null) => {
    try {
      return JSON.parse(str) || fallback;
    } catch {
      return fallback;
    }
  };

  useEffect(() => {
    // Load personal data
    const storedPersonal = localStorage.getItem('personalData');
    if (storedPersonal) {
      const data = safeParseJSON(storedPersonal, {});
      setPersonalData((prev) => ({
        ...prev,
        ...data,
        linkedIn: linkedIn || data.linkedIn || prev.linkedIn || '',
      }));
    }
    if (Object.keys(formData).length > 0) {
      setPersonalData((prev) => ({
        ...prev,
        ...formData,
        linkedIn: linkedIn || formData.linkedIn || prev.linkedIn || '',
      }));
    }

    // Summary
    const summaries = localStorage.getItem('summaries');
    if (summaries) {
      const arr = safeParseJSON(summaries, []);
      if (Array.isArray(arr) && arr.length > 0) setSavedSummary(arr[arr.length - 1]);
    }

    // Education
    const edu = localStorage.getItem('educationEntries');
    if (edu) setEducationEntries(safeParseJSON(edu, []));

    // Skills
    const sk = localStorage.getItem('skills');
    if (sk) setSavedSkills(safeParseJSON(sk, []));

    // Work
    const work = localStorage.getItem('workExperiences');
    if (work) setWorkExperiences(safeParseJSON(work, []));

    // Certifications
    const cert = localStorage.getItem('certificationsList');
    if (cert) setSavedCertifications(safeParseJSON(cert, []));

    // Languages
    const lang = localStorage.getItem('languagesList');
    if (lang) setSavedLanguages(safeParseJSON(lang, []));

    // Accomplishments
    const acc = localStorage.getItem('accomplishmentsList');
    if (acc) setSavedAccomplishments(safeParseJSON(acc, []));

    // Additional Info
    const add = localStorage.getItem('additionalInfo');
    if (add) setSavedAdditionalInfo(safeParseJSON(add, []));

    // Software
    const soft = localStorage.getItem('softwareSkills');
    if (soft) setSavedSoftware(safeParseJSON(soft, []));

    // References
    const ref = localStorage.getItem('referencesList');
    if (ref) setSavedReferences(safeParseJSON(ref, []));

    // Volunteering
    const vol = localStorage.getItem('volunteering');
    if (vol) setSavedVolunteering(safeParseJSON(vol, []));

    // Interests
    const int = localStorage.getItem('interests');
    if (int) setSavedInterests(safeParseJSON(int, []));

    // Websites
    const web = localStorage.getItem('websites');
    if (web) setSavedWebsites(safeParseJSON(web, []));

    // Custom sections
    const userId = localStorage.getItem('userId');
    if (userId) {
      const currentCustom = localStorage.getItem(`current_custom_sections_${userId}`);
      if (currentCustom) {
        const list = safeParseJSON(currentCustom, []);
        if (Array.isArray(list)) {
          const sectionsData = {};
          list.forEach((name) => {
            const key = `custom_${userId}_${name}`;
            const dataStr = localStorage.getItem(key);
            if (dataStr) {
              const parsed = safeParseJSON(dataStr, {});
              const items = (parsed.items || []).filter((v) => v && v.trim());
              if (items.length > 0) sectionsData[name] = items;
            }
          });
          setCustomSections(sectionsData);
        }
      }
    }

    // Section order
    const savedOrder = localStorage.getItem('resume27SectionOrder');
    if (savedOrder) {
      const order = safeParseJSON(savedOrder, defaultSectionOrder);
      setSectionOrder(order);
    }
  }, [formData, linkedIn]);

  useEffect(() => {
    const customIds = Object.keys(customSections).map((n) => `custom_${n}`);
    setSectionOrder((prev) => {
      const withoutCustom = prev.filter((s) => !s.startsWith('custom_'));
      const newOrder = [...withoutCustom, ...customIds];
      if (JSON.stringify(newOrder) !== JSON.stringify(prev)) {
        localStorage.setItem('resume27SectionOrder', JSON.stringify(newOrder));
        return newOrder;
      }
      return prev;
    });
  }, [customSections]);

  useEffect(() => {
    if (sectionOrder.length > 0) {
      localStorage.setItem('resume27SectionOrder', JSON.stringify(sectionOrder));
    }
  }, [sectionOrder]);

  // ── Pagination calculation ────────────────────────────────────────────────
  useEffect(() => {
    if (exporting) return;
    const container = measureRef.current;
    if (!container) return;

    const usableHeight = pageH - topBottomMargin * 2;
    const sections = container.querySelectorAll('.resume27-section');
    let accumulated = 0;
    let pageCount = 1;

    sections.forEach((section) => {
      const h = section.getBoundingClientRect().height;
      if (accumulated + h > usableHeight * pageCount) {
        pageCount++;
      }
      accumulated += h;
    });

    setTotalPages(Math.max(1, pageCount));
  }, [
    sectionOrder, savedSummary, workExperiences, educationEntries,
    savedSkills, savedLanguages, savedAccomplishments, savedCertifications,
    savedReferences, savedSoftware, savedVolunteering, savedInterests,
    savedWebsites, savedAdditionalInfo, customSections,
    topBottomMargin, sectionSpacing, paragraphSpacing, lineSpacing,
    fontSize, headingSize, pageH, exporting,
  ]);

  // ── Get sections for current page ────────────────────────────────────────
  const getSectionsForCurrentPage = () => {
    if (exporting || totalPages === 1) return sectionOrder;
    const container = measureRef.current;
    if (!container) return sectionOrder;

    const usableHeight = pageH - topBottomMargin * 2;
    const sections = container.querySelectorAll('.resume27-section');
    const sectionIds = [...sectionOrder];

    const pageChunks = [[]];
    let accumulated = 0;
    let pageIdx = 0;

    sections.forEach((section, i) => {
      const h = section.getBoundingClientRect().height;
      if (accumulated + h > usableHeight * (pageIdx + 1)) {
        pageIdx++;
        pageChunks.push([]);
      }
      if (sectionIds[i]) pageChunks[pageIdx].push(sectionIds[i]);
      accumulated += h;
    });

    return pageChunks[currentPage] || pageChunks[0] || sectionOrder;
  };

  // Drag & Drop
  const handleDragStart = (e, index) => {
    e.dataTransfer.setData('text/plain', index);
    e.currentTarget.style.opacity = '0.4';
  };
  const handleDragOver = (e, index) => { e.preventDefault(); setDragOverIndex(index); };
  const handleDrop = (e, index) => {
    e.preventDefault();
    const src = Number(e.dataTransfer.getData('text/plain'));
    if (src !== index) {
      const newOrder = [...sectionOrder];
      const [moved] = newOrder.splice(src, 1);
      newOrder.splice(index, 0, moved);
      setSectionOrder(newOrder);
    }
    setDragOverIndex(null);
  };
  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = '1';
    setDragOverIndex(null);
  };

  // ── Reset section order (from Resume30) ─────────────────────────────────
  const resetSectionOrder = () => {
    const customIds = Object.keys(customSections).map((n) => `custom_${n}`);
    const newOrder = [...defaultSectionOrder, ...customIds];
    setSectionOrder(newOrder);
    localStorage.setItem('resume27SectionOrder', JSON.stringify(newOrder));
  };

  // ── Section heading — uses headingSize ──────────────────────────────────
  const SectionHeading = ({ children }) => (
    <Typography
      sx={{
        fontWeight: 700,
        fontSize: headingSize,
        color: color,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        mb: `${paragraphSpacing * 0.5}px`,
        mt: 0,
        lineHeight: 1.3,
        fontFamily: font,
        fontStyle: fontStyle,
      }}
    >
      {children}
    </Typography>
  );

  // ── Bullet point ──────────────────────────────────────────────────────────
  const Bullet = () => (
    <Box
      sx={{
        width: '4px',
        height: '4px',
        borderRadius: '50%',
        bgcolor: color,
        flexShrink: 0,
        mt: '5.5px',
        mr: '6px',
      }}
    />
  );

  // ── Render each section ───────────────────────────────────────────────────
  const renderSectionContent = (sectionId) => {
    switch (sectionId) {

      case 'summary':
        if (!savedSummary) return null;
        return (
          <Box className="resume27-section" mb={`${sectionSpacing}px`}>
            <SectionHeading>Summary</SectionHeading>
            <Divider sx={{ mb: `${paragraphSpacing * 0.5}px`, borderColor: color, borderBottomWidth: `${lineWeight}px` }} />
            <Typography
              sx={{
                fontSize: fontSize,
                lineHeight: `${lineSpacing}px`,
                wordBreak: 'break-word',
                textAlign: 'justify',
                fontFamily: font,
                fontStyle: fontStyle,
              }}
            >
              {savedSummary}
            </Typography>
          </Box>
        );

      case 'work-experience':
        if (!workExperiences.length) return null;
        return (
          <Box className="resume27-section" mb={`${sectionSpacing}px`}>
            <SectionHeading>Work Experience</SectionHeading>
            <Divider sx={{ mb: `${paragraphSpacing * 0.5}px`, borderColor: color, borderBottomWidth: `${lineWeight}px` }} />
            {workExperiences.map((job, idx) => (
              <Box key={idx} mb={`${paragraphSpacing}px`}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '2px' }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: fontSize,
                        lineHeight: `${lineSpacing}px`,
                        wordBreak: 'break-word',
                        fontFamily: font,
                        fontStyle: fontStyle,
                      }}
                    >
                      {job.jobTitle || job.title || 'Position'}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: fontSize,
                        color: 'text.secondary',
                        lineHeight: `${lineSpacing}px`,
                        wordBreak: 'break-word',
                        fontFamily: font,
                      }}
                    >
                      {[job.employer || job.company, job.city, job.country].filter(Boolean).join(', ') || 'Company'}
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      fontSize: fontSize,
                      color: 'text.secondary',
                      fontStyle: 'italic',
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                      ml: '8px',
                      lineHeight: `${lineSpacing}px`,
                      fontFamily: font,
                    }}
                  >
                    {[job.startMonth, job.startYear].filter(Boolean).join(' ')}
                    {(job.startMonth || job.startYear) ? ' – ' : ''}
                    {job.current ? 'Present' : [job.endMonth, job.endYear].filter(Boolean).join(' ')}
                  </Typography>
                </Box>
                {job.description && (
                  <Box sx={{ mt: `${paragraphSpacing * 0.3}px`, pl: '2px' }}>
                    {job.description.split('\n').filter((l) => l.trim()).map((line, i) => (
                      <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', mb: '2px' }}>
                        <Bullet />
                        <Typography
                          sx={{
                            fontSize: fontSize,
                            lineHeight: `${lineSpacing}px`,
                            wordBreak: 'break-word',
                            flex: 1,
                            fontFamily: font,
                            fontStyle: fontStyle,
                          }}
                        >
                          {line.trim().replace(/^[•\-]\s*/, '')}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        );

      case 'education':
        if (!educationEntries.length) return null;
        return (
          <Box className="resume27-section" mb={`${sectionSpacing}px`}>
            <SectionHeading>Education</SectionHeading>
            <Divider sx={{ mb: `${paragraphSpacing * 0.5}px`, borderColor: color, borderBottomWidth: `${lineWeight}px` }} />
            {educationEntries.map((edu, idx) => (
              <Box key={idx} mb={`${paragraphSpacing}px`}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '2px' }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: fontSize,
                        lineHeight: `${lineSpacing}px`,
                        wordBreak: 'break-word',
                        fontFamily: font,
                        fontStyle: fontStyle,
                      }}
                    >
                      {edu.degreeName || edu.degree || 'Degree'}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: fontSize,
                        color: 'text.secondary',
                        lineHeight: `${lineSpacing}px`,
                        wordBreak: 'break-word',
                        fontFamily: font,
                      }}
                    >
                      {[edu.collegeName || edu.schoolName, edu.collegeLocation || edu.schoolLocation].filter(Boolean).join(', ') || 'University'}
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      fontSize: fontSize,
                      color: 'text.secondary',
                      fontStyle: 'italic',
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                      ml: '8px',
                      lineHeight: `${lineSpacing}px`,
                      fontFamily: font,
                    }}
                  >
                    {[edu.gradMonth, edu.gradYear].filter(Boolean).join(' ')}
                  </Typography>
                </Box>
                <Box sx={{ mt: '2px', pl: '8px' }}>
                  {edu.cgpa && <Typography sx={{ fontSize: fontSize, lineHeight: `${lineSpacing}px`, fontFamily: font }}>CGPA: {edu.cgpa}</Typography>}
                  {edu.percentage && <Typography sx={{ fontSize: fontSize, lineHeight: `${lineSpacing}px`, fontFamily: font }}>Percentage: {edu.percentage}%</Typography>}
                  {edu.grade && !edu.cgpa && !edu.percentage && <Typography sx={{ fontSize: fontSize, lineHeight: `${lineSpacing}px`, fontFamily: font }}>{edu.grade}</Typography>}
                  {edu.specialization && <Typography sx={{ fontSize: fontSize, lineHeight: `${lineSpacing}px`, fontFamily: font }}>{edu.specialization}</Typography>}
                </Box>
              </Box>
            ))}
          </Box>
        );

      case 'skills':
        if (!savedSkills.length) return null;
        return (
          <Box className="resume27-section" mb={`${sectionSpacing}px`}>
            <SectionHeading>Skills</SectionHeading>
            <Divider sx={{ mb: `${paragraphSpacing * 0.5}px`, borderColor: color, borderBottomWidth: `${lineWeight}px` }} />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: `${paragraphSpacing * 0.5}px 20px` }}>
              {savedSkills.map((s, i) => {
                const skillName = typeof s === 'string' ? s : (s.name || s.category || s.skill || '');
                const skillLevel = typeof s === 'object' ? (s.level || s.proficiency || null) : null;
                if (!skillName) return null;
                return (
                  <Box key={i} sx={{ minWidth: '100px', flex: '0 1 auto' }}>
                    <Typography
                      sx={{
                        fontSize: fontSize,
                        fontWeight: 600,
                        color: '#222',
                        lineHeight: `${lineSpacing}px`,
                        wordBreak: 'break-word',
                        fontFamily: font,
                        fontStyle: fontStyle,
                      }}
                    >
                      {skillName}
                    </Typography>
                    {skillLevel && (
                      <Box sx={{ height: `${lineWeight + 2}px`, bgcolor: '#e0e0e0', borderRadius: '2px', mt: '2px', width: '100%' }}>
                        <Box sx={{ height: '100%', width: `${skillLevel}%`, bgcolor: color, borderRadius: '2px' }} />
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>
        );

      case 'certificates':
      case 'certifications':
        if (!savedCertifications.length) return null;
        return (
          <Box className="resume27-section" mb={`${sectionSpacing}px`}>
            <SectionHeading>Certificates</SectionHeading>
            <Divider sx={{ mb: `${paragraphSpacing * 0.5}px`, borderColor: color, borderBottomWidth: `${lineWeight}px` }} />
            {savedCertifications.map((c, i) => (
              <Box key={i} mb={`${paragraphSpacing}px`}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '2px' }}>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: fontSize,
                      lineHeight: `${lineSpacing}px`,
                      wordBreak: 'break-word',
                      flex: 1,
                      fontFamily: font,
                      fontStyle: fontStyle,
                    }}
                  >
                    {c.name || c.certName || c.title || ''}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: fontSize,
                      color: 'text.secondary',
                      fontStyle: 'italic',
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                      ml: '8px',
                      lineHeight: `${lineSpacing}px`,
                      fontFamily: font,
                    }}
                  >
                    {c.year || c.date || ''}
                  </Typography>
                </Box>
                {(c.provider || c.organization || c.description) && (
                  <Typography
                    sx={{
                      fontSize: fontSize,
                      lineHeight: `${lineSpacing}px`,
                      wordBreak: 'break-word',
                      color: 'text.secondary',
                      fontFamily: font,
                    }}
                  >
                    {[c.provider || c.organization, c.description].filter(Boolean).join(' • ')}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        );

      case 'languages':
        if (!savedLanguages.length) return null;
        return (
          <Box className="resume27-section" mb={`${sectionSpacing}px`}>
            <SectionHeading>Languages</SectionHeading>
            <Divider sx={{ mb: `${paragraphSpacing * 0.5}px`, borderColor: color, borderBottomWidth: `${lineWeight}px` }} />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: `${paragraphSpacing * 0.3}px 24px` }}>
              {savedLanguages.map((lang, i) => {
                const langName = typeof lang === 'string' ? lang : (lang.language || lang.name || '');
                const langLevel = typeof lang === 'object' ? (lang.level || lang.proficiency || '') : '';
                return (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: '120px' }}>
                    <Bullet />
                    <Typography sx={{ fontSize: fontSize, lineHeight: `${lineSpacing}px`, fontFamily: font, fontStyle: fontStyle }}>
                      <span style={{ fontWeight: 600 }}>{langName}</span>
                      {langLevel ? ` — ${langLevel}` : ''}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        );

      case 'accomplishments':
        if (!savedAccomplishments.length) return null;
        return (
          <Box className="resume27-section" mb={`${sectionSpacing}px`}>
            <SectionHeading>Accomplishments</SectionHeading>
            <Divider sx={{ mb: `${paragraphSpacing * 0.5}px`, borderColor: color, borderBottomWidth: `${lineWeight}px` }} />
            {savedAccomplishments.map((item, i) => {
              const text = typeof item === 'string' ? item : (item.title || item.description || item.name || JSON.stringify(item));
              return (
                <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', mb: '3px' }}>
                  <Bullet />
                  <Typography sx={{ fontSize: fontSize, lineHeight: `${lineSpacing}px`, wordBreak: 'break-word', flex: 1, fontFamily: font, fontStyle: fontStyle }}>
                    {text}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        );

      case 'additional-info':
        if (!savedAdditionalInfo.length) return null;
        return (
          <Box className="resume27-section" mb={`${sectionSpacing}px`}>
            <SectionHeading>Additional Information</SectionHeading>
            <Divider sx={{ mb: `${paragraphSpacing * 0.5}px`, borderColor: color, borderBottomWidth: `${lineWeight}px` }} />
            {savedAdditionalInfo.map((item, i) => {
              const text = typeof item === 'string' ? item : (item.info || item.text || item.description || JSON.stringify(item));
              return (
                <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', mb: '3px' }}>
                  <Bullet />
                  <Typography sx={{ fontSize: fontSize, lineHeight: `${lineSpacing}px`, wordBreak: 'break-word', flex: 1, fontFamily: font, fontStyle: fontStyle }}>
                    {text}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        );

      case 'software':
        if (!savedSoftware.length) return null;
        return (
          <Box className="resume27-section" mb={`${sectionSpacing}px`}>
            <SectionHeading>Software</SectionHeading>
            <Divider sx={{ mb: `${paragraphSpacing * 0.5}px`, borderColor: color, borderBottomWidth: `${lineWeight}px` }} />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: `${paragraphSpacing * 0.3}px 20px` }}>
              {savedSoftware.map((item, i) => {
                const name = typeof item === 'string' ? item : (item.name || item.software || item.tool || '');
                return (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Bullet />
                    <Typography sx={{ fontSize: fontSize, lineHeight: `${lineSpacing}px`, fontFamily: font, fontStyle: fontStyle }}>{name}</Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        );

      case 'references':
        if (!savedReferences.length) return null;
        return (
          <Box className="resume27-section" mb={`${sectionSpacing}px`}>
            <SectionHeading>References</SectionHeading>
            <Divider sx={{ mb: `${paragraphSpacing * 0.5}px`, borderColor: color, borderBottomWidth: `${lineWeight}px` }} />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: `${paragraphSpacing * 0.5}px 24px` }}>
              {savedReferences.map((ref, i) => {
                const name = typeof ref === 'string' ? ref : (ref.name || ref.referenceName || '');
                const title = typeof ref === 'object' ? (ref.title || ref.position || '') : '';
                const contact = typeof ref === 'object' ? (ref.email || ref.phone || ref.contact || '') : '';
                return (
                  <Box key={i} sx={{ minWidth: '160px', flex: '0 1 auto' }}>
                    <Typography sx={{ fontWeight: 700, fontSize: fontSize, lineHeight: `${lineSpacing}px`, fontFamily: font }}>{name}</Typography>
                    {title && <Typography sx={{ fontSize: fontSize, color: 'text.secondary', lineHeight: `${lineSpacing}px`, fontFamily: font }}>{title}</Typography>}
                    {contact && <Typography sx={{ fontSize: fontSize, color: 'text.secondary', lineHeight: `${lineSpacing}px`, fontFamily: font }}>{contact}</Typography>}
                  </Box>
                );
              })}
            </Box>
          </Box>
        );

      case 'volunteering':
        if (!savedVolunteering.length) return null;
        return (
          <Box className="resume27-section" mb={`${sectionSpacing}px`}>
            <SectionHeading>Volunteering</SectionHeading>
            <Divider sx={{ mb: `${paragraphSpacing * 0.5}px`, borderColor: color, borderBottomWidth: `${lineWeight}px` }} />
            {savedVolunteering.map((item, i) => {
              const role = typeof item === 'string' ? item : (item.subtopic || item.role || item.title || item.position || '');
              const org = typeof item === 'object' ? (item.organization || item.org || '') : '';
              const desc = typeof item === 'object' ? (item.content || item.description || '') : '';
              const date = typeof item === 'object' ? ([item.fromDate, item.toDate].filter(Boolean).join(' - ') || item.date || item.year || '') : '';
              return (
                <Box key={i} mb={`${paragraphSpacing}px`}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: fontSize, lineHeight: `${lineSpacing}px`, fontFamily: font, fontStyle: fontStyle }}>{role}</Typography>
                      {org && <Typography sx={{ fontSize: fontSize, color: 'text.secondary', lineHeight: `${lineSpacing}px`, fontFamily: font }}>{org}</Typography>}
                    </Box>
                    {date && (
                      <Typography sx={{ fontSize: fontSize, color: 'text.secondary', fontStyle: 'italic', ml: '8px', flexShrink: 0, fontFamily: font }}>{date}</Typography>
                    )}
                  </Box>
                  {desc && (
                    <Typography sx={{ fontSize: fontSize, lineHeight: `${lineSpacing}px`, mt: '2px', wordBreak: 'break-word', fontFamily: font, fontStyle: fontStyle }}>{desc}</Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        );

      case 'interests':
        if (!savedInterests.length) return null;
        return (
          <Box className="resume27-section" mb={`${sectionSpacing}px`}>
            <SectionHeading>Interests</SectionHeading>
            <Divider sx={{ mb: `${paragraphSpacing * 0.5}px`, borderColor: color, borderBottomWidth: `${lineWeight}px` }} />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: `${paragraphSpacing * 0.3}px 16px` }}>
              {savedInterests.map((item, i) => {
                const text = typeof item === 'string' ? item : (item.interest || item.name || '');
                return (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Bullet />
                    <Typography sx={{ fontSize: fontSize, lineHeight: `${lineSpacing}px`, fontFamily: font, fontStyle: fontStyle }}>{text}</Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        );

      case 'websites':
        if (!savedWebsites.length) return null;
        return (
          <Box className="resume27-section" mb={`${sectionSpacing}px`}>
            <SectionHeading>Websites & Links</SectionHeading>
            <Divider sx={{ mb: `${paragraphSpacing * 0.5}px`, borderColor: color, borderBottomWidth: `${lineWeight}px` }} />
            {savedWebsites.map((item, i) => {
              const label = typeof item === 'string' ? item : (item.label || item.name || item.title || '');
              const url = typeof item === 'object' ? (item.url || item.link || '') : '';
              return (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: '4px', mb: '3px' }}>
                  <LanguageIcon sx={{ fontSize: fontSize, color, flexShrink: 0 }} />
                  <Typography sx={{ fontSize: fontSize, lineHeight: `${lineSpacing}px`, wordBreak: 'break-all', fontFamily: font, fontStyle: fontStyle }}>
                    {label}{url && label !== url ? ` — ${url}` : ''}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        );

      default:
        if (sectionId.startsWith('custom_')) {
          const name = sectionId.replace('custom_', '');
          const items = customSections[name] || [];
          if (!items.length) return null;
          return (
            <Box className="resume27-section" mb={`${sectionSpacing}px`}>
              <SectionHeading>{name.replace(/_/g, ' ')}</SectionHeading>
              <Divider sx={{ mb: `${paragraphSpacing * 0.5}px`, borderColor: color, borderBottomWidth: `${lineWeight}px` }} />
              {items.map((item, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', mb: '3px' }}>
                  <Bullet />
                  <Typography sx={{ fontSize: fontSize, lineHeight: `${lineSpacing}px`, wordBreak: 'break-word', flex: 1, fontFamily: font, fontStyle: fontStyle }}>{item}</Typography>
                </Box>
              ))}
            </Box>
          );
        }
        return null;
    }
  };

  // Display values — formData takes priority over localStorage
  const displayName = `${(formData.firstName || personalData.firstName || 'AVERY').toUpperCase()} ${(formData.lastName || personalData.lastName || 'DAVIS').toUpperCase()}`;
  const displayCity = formData.city || personalData.city || '';
  const displayEmail = formData.email || personalData.email || '';
  const displayPhone = formData.phone || personalData.phone || '';
  const displayLinkedIn = formData.linkedIn || personalData.linkedIn || linkedIn || '';
  const displayPosition = formData.currentPosition || personalData.currentPosition || '';
  const displayWebsite = formData.website || personalData.website || '';

  const pageStyle = {
    width: `${pageW}px`,
    minHeight: `${pageH}px`,
    maxWidth: `${pageW}px`,
    margin: '0 auto',
    backgroundColor: '#ffffff',
    boxSizing: 'border-box',
    fontFamily: font,
    fontStyle: fontStyle,
    fontSize: fontSize,
    color: '#222',
    padding: `${topBottomMargin}px ${sideMargins}px`,
    overflowX: 'hidden',
    wordBreak: 'break-word',
    ...(exporting ? { overflow: 'hidden', height: `${pageH}px` } : {}),
  };

  return (
    <Box sx={{ position: 'relative' }}>
      {/* Page controls */}
      {!exporting && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <IconButton
            onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
            disabled={currentPage === 0}
            size="small"
          >
            <ChevronLeft />
          </IconButton>
          <Typography variant="body2">Page {currentPage + 1} / {totalPages}</Typography>
          <IconButton
            onClick={() => setCurrentPage((p) => Math.min(totalPages - 1, p + 1))}
            disabled={currentPage >= totalPages - 1}
            size="small"
          >
            <ChevronRight />
          </IconButton>
          {enableDragDrop && (
            <>
              <Button variant="contained" size="small" startIcon={<Reorder />} onClick={() => setArrangeDialogOpen(true)}>
                Arrange Sections
              </Button>
              {/* ── Reset All Sections button (from Resume30) ── */}
              <Button variant="outlined" size="small" color="secondary" onClick={resetSectionOrder}>
                Reset All Sections
              </Button>
            </>
          )}
        </Box>
      )}

      {/* Hidden full content for height measurement */}
      <Box
        ref={measureRef}
        sx={{
          position: 'absolute',
          visibility: 'hidden',
          pointerEvents: 'none',
          width: `${pageW}px`,
          top: 0,
          left: 0,
          zIndex: -1,
          padding: `${topBottomMargin}px ${sideMargins}px`,
          boxSizing: 'border-box',
        }}
      >
        {sectionOrder.map((id) => (
          <React.Fragment key={id}>{renderSectionContent(id)}</React.Fragment>
        ))}
      </Box>

      {/* A4 Page */}
      <Box
        component={Paper}
        elevation={exporting ? 0 : 3}
        className="resume-page"
        sx={{
          ...pageStyle,
          boxShadow: exporting ? 'none' : '0 4px 24px rgba(0,0,0,0.13)',
        }}
      >
        {/* ── HEADER ── */}
        {(currentPage === 0 || exporting) && (
          <Box
            sx={{
              textAlign: 'center',
              mb: `${sectionSpacing}px`,
              pb: `${paragraphSpacing}px`,
              borderBottom: `${lineWeight + 1.5}px solid ${color}`,
            }}
          >
            <Typography
              sx={{
                fontFamily: font,
                fontStyle: fontStyle,
                fontWeight: 800,
                fontSize: headingSize,
                letterSpacing: '0.12em',
                color: '#111',
                lineHeight: 1.15,
                wordBreak: 'break-word',
              }}
            >
              {displayName}
            </Typography>

            {displayPosition && (
              <Typography
                sx={{
                  fontSize: fontSize,
                  color: color,
                  fontWeight: 600,
                  letterSpacing: '0.07em',
                  mt: `${paragraphSpacing * 0.3}px`,
                  textTransform: 'uppercase',
                  fontFamily: font,
                  fontStyle: fontStyle,
                }}
              >
                {displayPosition}
              </Typography>
            )}

            {/* Contact row */}
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                alignItems: 'center',
                gap: `${paragraphSpacing * 0.3}px 14px`,
                mt: `${paragraphSpacing * 0.8}px`,
                color: '#444',
              }}
            >
              {displayCity && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                  <LocationOnIcon sx={{ fontSize: fontSize, color }} />
                  <Typography sx={{ fontSize: fontSize, lineHeight: 1, fontFamily: font }}>{displayCity}</Typography>
                </Box>
              )}
              {displayEmail && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                  <EmailIcon sx={{ fontSize: fontSize, color }} />
                  <Typography sx={{ fontSize: fontSize, lineHeight: 1, wordBreak: 'break-all', fontFamily: font }}>{displayEmail}</Typography>
                </Box>
              )}
              {displayPhone && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                  <PhoneIcon sx={{ fontSize: fontSize, color }} />
                  <Typography sx={{ fontSize: fontSize, lineHeight: 1, fontFamily: font }}>{displayPhone}</Typography>
                </Box>
              )}
              {displayLinkedIn && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                  <LinkedInIcon sx={{ fontSize: fontSize, color }} />
                  <Typography sx={{ fontSize: fontSize, lineHeight: 1, wordBreak: 'break-all', fontFamily: font }}>{displayLinkedIn}</Typography>
                </Box>
              )}
              {displayWebsite && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                  <LanguageIcon sx={{ fontSize: fontSize, color }} />
                  <Typography sx={{ fontSize: fontSize, lineHeight: 1, wordBreak: 'break-all', fontFamily: font }}>{displayWebsite}</Typography>
                </Box>
              )}
            </Box>
          </Box>
        )}

        {/* ── SECTIONS ── */}
        <Box sx={{ width: '100%', overflowX: 'hidden', wordBreak: 'break-word' }}>
          {getSectionsForCurrentPage().map((id) => (
            <React.Fragment key={id}>{renderSectionContent(id)}</React.Fragment>
          ))}
        </Box>
      </Box>

      {/* Arrange Sections Dialog */}
      <Dialog open={arrangeDialogOpen} onClose={() => setArrangeDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Arrange Resume Sections</DialogTitle>
        <DialogContent>
          <Box sx={{ minHeight: 300, pt: 1 }}>
            {sectionOrder.map((id, index) => (
              <Paper
                key={id}
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDrop={(e) => handleDrop(e, index)}
                onDragEnd={handleDragEnd}
                sx={{
                  p: 1.5,
                  mb: 1,
                  display: 'flex',
                  alignItems: 'center',
                  bgcolor: dragOverIndex === index ? '#f0f8ff' : 'white',
                  border: dragOverIndex === index ? `2px dashed ${color}` : '1px solid #ddd',
                  cursor: 'grab',
                  userSelect: 'none',
                  transition: 'all 0.15s ease',
                  '&:hover': { bgcolor: '#fafafa' },
                }}
              >
                <DragIndicator sx={{ mr: 2, color: '#999' }} />
                <Typography sx={{ flexGrow: 1, textTransform: 'capitalize', fontSize: '13px' }}>
                  {id.replace(/-/g, ' ').replace('custom_', '').replace(/_/g, ' ')}
                </Typography>
              </Paper>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetSectionOrder} color="inherit">Reset Order</Button>
          <Button variant="contained" onClick={() => setArrangeDialogOpen(false)}>Done</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Resume27;